import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LogoDark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="mb-8"><LogoDark /></div>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">Set a new password</h1>
        <p className="text-slate-500 mt-1 mb-6 text-sm">Choose a strong password you'll remember.</p>
        {!token ? (
          <p data-testid="reset-invalid" className="text-sm text-red-600">This reset link is invalid or missing a token.</p>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password">New password</Label>
              <Input id="password" data-testid="reset-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required minLength={8} className="h-11" placeholder="••••••••" />
            </div>
            {error && <div data-testid="reset-error" className="text-sm text-red-600">{error}</div>}
            <Button data-testid="reset-submit-button" type="submit" disabled={loading}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
              {loading ? "Resetting…" : "Reset password"}
            </Button>
          </form>
        )}
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-medium text-cyan-700 hover:text-cyan-900 mt-6">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
      </motion.div>
    </div>
  );
}
